<script setup lang="ts">
/**
 * 后台外壳 —— **菜单放左的卡片式布局**（2026-08-06 按设计稿定稿重做）。
 *
 * <h2>🔴 上一版是「深色顶栏 + 横向导航」，为什么整个换掉</h2>
 * ① 深色顶栏那一条压在每一屏最上面，而这个产品**一屏全是数字**。
 *    大面积深色会让每个数都像在喊，反而分不出哪个该看。
 * ② 横向导航的宽度是**有限且会用完的**：现在 6 项已经占掉半条顶栏，
 *    订单、商品、结算、通知这些域一接上来就放不下 —— 到时候不是加「更多」下拉，
 *    就是重做，⛔ 而「更多」下拉里的东西等于没有。左侧菜单是纵向的，加项不涨宽。
 *
 * ⇒ 现在是：**浅灰地面 + 白卡面**。左菜单是一张卡，内容区是另一片区域，
 *    靠**面**区分层级，⛔ 不靠一堆分隔线 —— 线越多越看不出层级。
 *
 * <h2>⚠️ 令牌全部来自 `styles/global.css`，⛔ 这里不写死任何色值与圆角</h2>
 * 那份文件里有一条硬规矩：**蓝底配墨字，⛔ 绝不蓝底蓝字**。
 * 选中的菜单项因此是「底变蓝 + 字保持墨色 + 一圈描边」，
 * ⛔ 不是「淡蓝底 + 深蓝字」—— 那样字会糊在底里，整块读作"一片蓝"，
 * 而不是"这一项被选中了"。
 */
import { computed, defineAsyncComponent, onMounted, onUnmounted, reactive, ref, watch, type Component } from 'vue'
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router'
import { useMessage } from 'naive-ui'
import { currentSession, onAuthChanged } from '@/api/tokens'
import { logout } from '@/api/auth'
import { MAX_CACHED_VIEWS, useTabsStore } from '@/stores/tabs'
import TabBar from '@/components/TabBar.vue'
import { loadPhase2Features, resetPhase2FeatureCache, type Phase2Feature, type Phase2FeatureState } from '@/api/phase2Features'
import { getSellerExtensionRelease, type ExtensionReleaseResult } from '@/api/extensionRelease'
import { downloadVerifiedExtension } from '@/downloads/extensionArtifact'

const route = useRoute()
const router = useRouter()
const message = useMessage()
const tabs = useTabsStore()

const session = ref(currentSession())
// Cache belongs to a principal, not an access token: rotations keep drafts, account changes discard old views.
const viewOwner = computed(() => session.value ? JSON.stringify([
  session.value.subjectType, session.value.userId, session.value.tenantId, session.value.subjectId,
]) : 'anonymous')
watch(viewOwner, () => tabs.reset(), { flush: 'sync' })
const isOperator = computed(() => session.value?.subjectType === 'OPERATOR')
const isForwarder = computed(() => session.value?.subjectType === 'FORWARDER')
const hasTenantNotifications = computed(() => session.value?.subjectType === 'TENANT')

/**
 * 卖家（租户）视角 —— 顶栏常驻 chips 的显示判据。
 *
 * 🔴 只看「当前不是运营 / 不是货代」，与左栏卖家菜单（`nav v-else`）**同一个判据**：
 * chip 指向的 `/billing`（套餐与用量，`meta.subject === 'TENANT'`）和 `/wallet` 只对卖家成立，
 * 给运营/货代摆一个必然报错的入口违反「别走进一个必然报错的界面」。
 */
const isSellerView = computed(() => !isOperator.value && !isForwarder.value)
const commercialPilotEnabled = ref(false)
const assistantPilotEnabled = ref(false)
const phase2 = ref<Phase2FeatureState>({ SELECTION_ANALYTICS:false, ALIBABA1688:false,
  AUTO_PROMOTION_REMOVAL:false, ADVANCED_MESSAGING:false, SALES_SCREEN:false })
const phase2Unavailable = ref(false)
const phase2Loading = ref(false)
/** 登录页不套后台外壳 */
const blank = computed(() => route.meta.layout === 'blank')

/**
 * 🔴 货代端现在有<b>两套</b>页面，外壳范式不同，必须在这一层分流：
 * <ul>
 *   <li><b>PWA 作业页</b>（`/tasks` 及其子路由 `/work/:id` / `/drafts` / `/fwd-settlement` / `/gaps`）
 *       —— 仓库现场单手用的暗色作业页，由 {@link ForwarderFrame} 自己套 `.fwd-scope` 暗色主题
 *       + 顶栏 + 底 tabbar。⇒ 这一族<b>不套</b>卖家端这套左菜单 + 多页签（两套范式硬叠只会两边都变样）。</li>
 *   <li><b>桌面后台页</b>（`/fwd/settlement` / `/fwd/payee`）—— 给货代老板 / 财务用的桌面管理后台，
 *       与卖家 / 运营后台同型（左菜单 + 多页签）。doc/59 §零：货代管理后台复用 seller 应用。</li>
 * </ul>
 * ⇒ 这一份 computed 决定「当前这一屏走哪套外壳」：
 *    货代登录后落到 `/fwd/settlement`（桌面后台），走左菜单；
 *    从菜单切去 `/tasks`（PWA 作业）则走 ForwarderFrame 的全屏外壳。
 * ⚠️ 路径判别刻意白名单化：PWA 那一摊是固定的几条路径，新增 PWA 页要在这里登记。
 */
const FWD_PWA_PATHS = new Set(['/tasks', '/drafts', '/fwd-settlement', '/gaps'])
function isForwarderPwaPath(): boolean {
  if (!isForwarder.value) return false
  if (FWD_PWA_PATHS.has(route.path)) return true
  // `/work/:orderId` 子路由
  if (route.path.startsWith('/work/')) return true
  return false
}
const forwarderPwa = computed(() => isForwarderPwaPath())

// ---- 插件下载（竞品 f-088 顶栏同义位：常驻入口；官网/后台同一 SHA-256 核验链路）----
const extensionDownloading = ref(false)
const extensionGuideVisible = ref(false)
const extensionGuideVersion = ref('')
async function downloadExtension(): Promise<void> {
  if (extensionDownloading.value) return
  extensionDownloading.value = true
  try {
    const release: ExtensionReleaseResult = await getSellerExtensionRelease()
    if (!release.metadata) {
      message.warning(release.warning ?? '插件版本暂不可核验，稍后再试')
      return
    }
    await downloadVerifiedExtension(release.metadata)
    message.success(`插件 ${release.metadata.version} 已开始下载（经 SHA-256 核验）`)
    // 🔴 安装/更新指引：Chrome 对同 ID 扩展不自动替换、zip 必须解压后加载文件夹——
    // 没有这段指引时用户装完旧版永远换不上新（2026-09-10 用户实测踩坑）。
    extensionGuideVersion.value = release.metadata.version
    extensionGuideVisible.value = true
  } catch (e) {
    message.error(e instanceof Error ? e.message : '插件文件核验失败')
  } finally {
    extensionDownloading.value = false
  }
}

// ---- 通知未读数：60 秒轮询 ----
// 浏览器原生 EventSource 不能携带本项目所需的 Authorization 请求头；
// 本项目又刻意使用 sessionStorage Bearer，而不是认证 cookie。直接 new EventSource
// 会稳定得到 401/500，并且服务端当前也没有事件驱动的后续推送。
// 因此这里采用真实可用、行为诚实的轮询，直到后端提供一次性 SSE ticket 或
// 支持带 Authorization 的 fetch-stream 客户端与事件驱动推送后再恢复长连接。
const unreadCount = ref(0)
let pollTimer: ReturnType<typeof setInterval> | null = null

async function pollUnread(): Promise<void> {
  if (!hasTenantNotifications.value) return
  try {
    const { getUnreadCount } = await import('@/api/notification')
    const data = await getUnreadCount()
    unreadCount.value = data.count
  } catch {
    // 静默失败：通知轮询不该弹错误干扰用户
  }
}

function startPolling(): void {
  if (!hasTenantNotifications.value || pollTimer) return
  pollUnread()
  pollTimer = setInterval(pollUnread, 60_000)
}

function stopPolling(): void {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null }
  unreadCount.value = 0
}

async function loadCommercialPilot(): Promise<void> {
  if (!isOperator.value) { commercialPilotEnabled.value = false; return }
  try {
    const { getCommercialExtendedPilot } = await import('@/api/commercialExtended')
    commercialPilotEnabled.value = (await getCommercialExtendedPilot()).enabled
  } catch { commercialPilotEnabled.value = false }
}

async function loadAssistantPilot(): Promise<void> {
  if (!hasTenantNotifications.value) { assistantPilotEnabled.value = false; return }
  try {
    const { getNativeAssistantStatus } = await import('@/api/nativeAssistant')
    assistantPilotEnabled.value = (await getNativeAssistantStatus()).enabled
  } catch { assistantPilotEnabled.value = false }
}

let phase2LoadEpoch = 0
async function loadPhase2(): Promise<boolean> {
  const epoch = ++phase2LoadEpoch
  phase2Loading.value = true
  phase2Unavailable.value = false
  phase2.value = { SELECTION_ANALYTICS:false, ALIBABA1688:false,
    AUTO_PROMOTION_REMOVAL:false, ADVANCED_MESSAGING:false, SALES_SCREEN:false }
  const state = await loadPhase2Features(session.value?.subjectType, () => {
    if (epoch === phase2LoadEpoch) phase2Unavailable.value = true
  })
  if (epoch !== phase2LoadEpoch) return false
  phase2.value = state
  phase2Loading.value = false
  return !phase2Unavailable.value
}

async function retryPhase2(): Promise<void> {
  if (phase2Loading.value) return
  resetPhase2FeatureCache()
  if (!await loadPhase2()) return
  const target = route.query.featureRetry
  const { featureRetry: _removed, ...query } = route.query
  if (typeof target === 'string' && target.startsWith('/') && !target.startsWith('//')) {
    const feature = router.resolve(target).meta.phase2Feature as Phase2Feature | undefined
    if (feature && phase2.value[feature]) { await router.replace(target); return }
    if (feature) message.info('此功能当前未开放。')
  }
  if (target != null) await router.replace({ path: route.path, query, hash: route.hash })
}

/**
 * 🔴 卖家端两级菜单（竞品对标 f-050/f-088，规格 §8：分组可折叠，进哪页展开哪组）。
 * 组的展开态是 UI 偏好 → localStorage；路由进入某组的任一子页时自动展开该组（竞品同行为），
 * ⛔ 不自动收起其它组 —— 用户手动收的组不该被路由悄悄翻回去。
 */
const SELLER_MENU_GROUPS = {
  products: '/products',
  selection: '/selection',
  // 订单消息（B7）：/messages（模板）与 /messages/records（发送记录）；大屏也挂订单组
  orders: ['/orders', '/returns', '/messages', '/messages/records', '/datascreen'],
  // 促销组（B8 三页 + phase2 自动移出）：三页常驻（Pilot 只关写不关看）
  promotion: ['/promotions', '/phase2/auto-promotion-removal'],
  shops: ['/shops', '/shop-groups'],
  sourcing: ['/sourcing/1688/auth', '/sourcing/1688'],
  tools: ['/pricing', '/tools', '/wallet', '/billing'],
} as const
type MenuGroupKey = keyof typeof SELLER_MENU_GROUPS
const MENU_OPEN_STORAGE = 'saaserp.seller.menuOpen.v1'

function loadMenuOpen(): Record<MenuGroupKey, boolean> {
  const fresh: Record<MenuGroupKey, boolean> = {
    products: false, selection: false, sourcing: false, promotion: false,
    orders: false, shops: false, tools: false,
  }
  try {
    const saved = JSON.parse(localStorage.getItem(MENU_OPEN_STORAGE) ?? '{}') as Partial<Record<MenuGroupKey, boolean>>
    for (const k of Object.keys(fresh) as MenuGroupKey[]) {
      if (typeof saved[k] === 'boolean') fresh[k] = saved[k] as boolean
    }
  } catch { /* 损坏的偏好当没有 */ }
  return fresh
}

const menuOpen = reactive(loadMenuOpen())

function persistMenuOpen(): void {
  try { localStorage.setItem(MENU_OPEN_STORAGE, JSON.stringify(menuOpen)) } catch { /* 私有模式等场景静默 */ }
}

function toggleMenuGroup(group: MenuGroupKey): void {
  menuOpen[group] = !menuOpen[group]
  persistMenuOpen()
}

/** 路由是否落在这组的任一前缀下（组值=单前缀或前缀数组） */
function routeInGroup(paths: string | readonly string[], path: string): boolean {
  const list = typeof paths === 'string' ? [paths] : paths
  return list.some((p) => path.startsWith(p))
}

watch(
  () => route.path,
  (path) => {
    if (!hasTenantNotifications.value) return
    let changed = false
    for (const [group, paths] of Object.entries(SELLER_MENU_GROUPS) as [MenuGroupKey, string | readonly string[]][]) {
      if (routeInGroup(paths, path) && !menuOpen[group]) { menuOpen[group] = true; changed = true }
    }
    if (changed) persistMenuOpen()
  },
  { immediate: true },
)

/**
 * 🔴 路由 → tab 同步：浏览器前进 / 后退 / 直接改 URL / 列表点详情，都靠这一个 watch。
 *
 * 路由的 `meta.title` 是 tab 标题来源（router/index.ts 每条路由都写了 title）。
 * `syncFromRoute` 内部会把子页面（如 `/orders/123`）折回一级 tab（`/orders`），
 * ⇒ 详情页在「订单」tab 内切换，⛔ 不爆新 tab。
 * 超 MAX_TABS 返回 false ⇒ 弹提示（并停留在当前 tab，因为新 tab 没建成）。
 */
watch(
  () => route.fullPath,
  () => {
    if (blank.value) return
    // 🔴 货代端 <b>PWA 作业页</b>（/tasks 及其子路由）不走多 Tab：
    //    那一摊是仓库作业页（顶栏 + 底部 tabbar，见 ForwarderFrame），与桌面管理后台的
    //    左菜单 + 多页签是两套范式。让它进 Tab 系统只会造出一堆 key 折回 /workbench 的脏 tab。
    // ⚠️ 但货代端<b>桌面后台页</b>（/fwd/settlement / /fwd/payee）走多 Tab ——
    //    它们与卖家 / 运营后台同型，且路径已在 TOP_LEVEL_PATHS 白名单里。
    if (forwarderPwa.value) return
    const title = typeof route.meta.title === 'string' ? route.meta.title : ''
    const ok = tabs.syncFromRoute(route.path, title, route.fullPath)
    if (!ok) {
      message.warning('打开的页签太多了，先关掉一些再开新的')
    }
  },
  { immediate: true },
)

/**
 * 🔴 keep-alive 的 `:include` —— 谁被缓存，只由 store 说了算（{@link tabs.cacheableNames}）：
 * 「打开中的页签 × 白名单内的路由」⇒ 组件名列表。模板里有五条更细的理由，这里只记两个坑：
 *
 * ⚠️ ① 必须先读一次 `route.fullPath`：vue-router 是**导航完成时**才把懒加载组件写回
 *    `record.components.default` 的（`extractComponentsGuards`），而 `router.getRoutes()`
 *    不是响应式的 —— 不把「路由变了」读进依赖，第一次进某个页签时组件名还没写回，
 *    那一页就会被漏掉缓存（表现为「这一页怎么切回来还是重挂」的偶发 bug）。
 * ⚠️ ② `tabs.tabs` 也读了（在 cacheableNames 里）：关闭页签 ⇒ 名单收缩 ⇒
 *    KeepAlive 的 include 变化 ⇒ 立即 pruneCache 掉该页缓存（驱逐机制就这一条）。
 */
const cachedViewNames = computed(() => {
  void route.fullPath
  return tabs.cacheableNames(router.getRoutes())
})

let stop: (() => void) | null = null
onMounted(() => {
  stop = onAuthChanged(() => {
    session.value = currentSession()
    if (hasTenantNotifications.value) startPolling()
    else stopPolling()
    void loadCommercialPilot()
    void loadAssistantPilot()
    void loadPhase2()
  })
  if (hasTenantNotifications.value) startPolling()
  void loadCommercialPilot()
  void loadAssistantPilot()
  void loadPhase2()
})
onUnmounted(() => {
  ++phase2LoadEpoch
  stop?.()
  stopPolling()
})

const home = computed(() => {
  if (isOperator.value) return '/ops/recharges'
  // 🔴 货代首页 = 我的结算单（桌面后台首页），不再是 PWA 作业入口 `/tasks`。
  //    doc/59 §零：货代管理后台复用 seller 应用，货代老板 / 财务登录后看的是结算单 + 收款信息。
  //    PWA 作业入口 `/tasks` 仍可从左侧菜单进去（仓库现场用）。
  if (isForwarder.value) return '/fwd/settlement'
  return '/workbench'
})

const who = computed(() => {
  const s = session.value
  if (!s) return null
  // ⚠️ 令牌里刻意不放昵称/手机号，且后端**没有 `/me` 端点**
  //    ⇒ 只能显示主体标识。⛔ 不要编一个名字出来。
  if (s.subjectType === 'OPERATOR') return '平台运营'
  if (s.subjectType === 'FORWARDER') {
    // 🔴 ForwarderSession 里只有 subjectId（forwarder_id），没有货代名
    return s.subjectId === null ? '货代' : `货代 #${s.subjectId}`
  }
  return s.tenantId === null ? '未知主体' : `租户 ${s.tenantId}`
})

async function doLogout() {
  await logout()
  await router.replace('/login')
}

/**
 * 🔴 开发期身份条 —— **只在开发构建里存在**。
 *
 * ⚠️ 写成 `import DevTenantBar from '...'` + `v-if="isDev"` 是不够的：
 * 那样组件代码**照样打进生产产物**，只是不渲染。
 * 实测过 —— 那一版的 dist 里能 grep 到 `X-Tenant-Id` 和这个条的中文文案。
 * ⇒ 必须让 `import()` 落在一个构建期就为 false 的分支里，打包器才会整个丢掉它。
 */
const DevTenantBar: Component | null = import.meta.env.DEV
  ? defineAsyncComponent(() => import('@/components/DevTenantBar.vue'))
  : null

/** 🔴 已登录时不再显示开发期身份条 —— 见 api/http.ts：两者同时存在会让身份来源变得不确定 */
const showDevBar = computed(() => DevTenantBar !== null && session.value === null)
</script>

<template>
  <router-view v-if="blank" />

  <!-- 🔴 货代端 <b>PWA 作业页</b>（/tasks 及其子路由）：仓库作业页范式（暗色、顶栏、底部 tabbar），
       由 ForwarderFrame 自己提供外壳。⛔ 不套卖家端这套左菜单 + 多页签 —— 两套范式硬叠只会两边都变样。 -->
  <router-view v-else-if="isForwarder && forwarderPwa" />

  <div v-else class="root">
    <component :is="DevTenantBar" v-if="showDevBar" />

    <div class="shell">
      <!-- ══ 左菜单：一张卡 ═══════════════════════════════════════════ -->
      <aside class="nav">
        <!-- 🔴 标志回**首页 = 工作台**（运营则回他唯一那一屏）—— 理由见 router/index.ts -->
        <RouterLink :to="home" class="brand">
          <!--
            标记：一个「不太相等的等号」—— 上面那条短一截。
            🔴 它就是产品那句话本身：「把这一单到底赚不赚**算清楚**」，
               两边对上了才算数。⛔ 不要换成通用的方块/盾牌图标。
          -->
          <svg class="mark" viewBox="0 0 46 26" aria-hidden="true">
            <defs>
              <!-- 2026-09-18 V3：品牌蓝→青渐变（admin-console V3 基准同款） -->
              <linearGradient id="jh-brand-grad" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="#1677ff" />
                <stop offset="1" stop-color="#22d3ee" />
              </linearGradient>
            </defs>
            <line x1="3" y1="8" x2="34" y2="8" />
            <line x1="3" y1="19" x2="43" y2="19" />
          </svg>
          <!-- 🔴 品牌名不带平台名 —— 理由见 LoginView.vue 里那段注释 -->
          <b>简界</b>
        </RouterLink>

        <!-- 🔴 按主体切导航：运营看不到卖家的页面，反之亦然。
             ⚠️ 这是"别走进一个必然报错的界面"，⛔ 不是权限 —— 权限在后端。
             🔴 点菜单 = 开 / 激活 tab（tabs.open）+ 导航（RouterLink 默认 push）。
                active 态用 tab 的 key 判，⛔ 不再用 router-link-active ——
                因为 `/orders/123` 这种子路由不会让 `/orders` 那项亮起来。 -->
        <nav v-if="isOperator" class="menu">
          <span class="sec">资金</span>
          <RouterLink to="/ops/recharges" :class="{ on: tabs.activeKey === '/ops/recharges' }" @click="tabs.open('/ops/recharges')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 4h10v8H3z" /><path d="M3 6h10" /></svg>
            <span>充值确认</span>
          </RouterLink>
          <RouterLink to="/ops/forwarder-settlements" :class="{ on: tabs.activeKey === '/ops/forwarder-settlements' }" @click="tabs.open('/ops/forwarder-settlements')">
            <!-- 货代结算💸：结算单（向右箭头表收/支）—— 与「充值确认」同属资金，配同一组线条风格 -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3h10v10H3z" /><path d="M3 7h10" /><path d="M6 10l-1.2 1.2" /><path d="M10 10l1.2 1.2" /><path d="M6 10h4" /></svg>
            <span>货代结算</span>
          </RouterLink>
          <span class="sec">审核</span>
          <RouterLink to="/ops/forwarders" :class="{ on: tabs.activeKey === '/ops/forwarders' }" @click="tabs.open('/ops/forwarders')">
            <!-- 货代审核✅：勾选框（资质拍板） -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3 3h10v10H3z" /><path d="M5.5 8.2l1.8 1.8 3.4-3.8" /></svg>
            <span>货代审核</span>
          </RouterLink>
          <RouterLink to="/ops/tenants" :class="{ on: tabs.activeKey === '/ops/tenants' }" @click="tabs.open('/ops/tenants')">
            <!-- 租户审核👤：人像 + 勾（注册拍板） -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="8" cy="6" r="2.4" /><path d="M3 13.5c.6-2.2 2.6-3.6 5-3.6s4.4 1.4 5 3.6" /><path d="M11.5 4l1.2 1.2L15 3" /></svg>
            <span>租户审核</span>
          </RouterLink>
          <span class="sec">统计</span>
          <RouterLink to="/ops/reports" :class="{ on: tabs.activeKey === '/ops/reports' }" @click="tabs.open('/ops/reports')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13V4" /><path d="M6.5 13V8" /><path d="M10 13V6" /><path d="M2.5 13.5h11" /></svg>
            <span>报表看板</span>
          </RouterLink>
          <RouterLink to="/ops/profit-disputes" :class="{ on: tabs.activeKey === '/ops/profit-disputes' }" @click="tabs.open('/ops/profit-disputes')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3h10v10H3z" /><path d="M5 6h6M5 9h4" /></svg>
            <span>利润异议</span>
          </RouterLink>
          <RouterLink to="/ops/returns" :class="{ on: tabs.activeKey === '/ops/returns' }" @click="tabs.open('/ops/returns')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3h10v10H3z" /><path d="M5 6h6M5 9h4" /><path d="m10 11 2-2-2-2" /></svg>
            <span>取消退货</span>
          </RouterLink>
          <span class="sec">商业化</span>
          <RouterLink to="/ops/price-plans" :class="{ on: tabs.activeKey === '/ops/price-plans' }" @click="tabs.open('/ops/price-plans')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3h7l4 4-6.5 6.5-4.5-4.5z" /><circle cx="6" cy="6.5" r="1" /></svg>
            <span>价格方案</span>
          </RouterLink>
          <RouterLink to="/ops/subscriptions" :class="{ on: tabs.activeKey === '/ops/subscriptions' }" @click="tabs.open('/ops/subscriptions')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><rect x="2.5" y="3" width="11" height="10" rx="1" /><path d="M2.5 6.5h11M5 9h6" /></svg>
            <span>订阅管理</span>
          </RouterLink>
          <RouterLink to="/ops/entitlements" :class="{ on: tabs.activeKey === '/ops/entitlements' }" @click="tabs.open('/ops/entitlements')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2.5 13 4v3.8c0 3-2 4.8-5 5.7-3-.9-5-2.7-5-5.7V4z" /><path d="m5.5 8 1.5 1.5 3.5-3.5" /></svg>
            <span>特殊权益</span>
          </RouterLink>
          <RouterLink to="/ops/unit-economics" :class="{ on: tabs.activeKey === '/ops/unit-economics' }" @click="tabs.open('/ops/unit-economics')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 13V8M7 13V5M11 13V3" /><path d="M2 13.5h12" /></svg>
            <span>单位经济</span>
          </RouterLink>
          <RouterLink v-if="commercialPilotEnabled" to="/ops/commercial-extended" :class="{ on: tabs.activeKey === '/ops/commercial-extended' }" @click="tabs.open('/ops/commercial-extended')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3h11v10h-11z" /><path d="M5 6h6M5 9h4" /></svg>
            <span>Phase2 商业化</span>
          </RouterLink>
          <span class="sec">配置</span>
          <RouterLink to="/ops/extension-releases" :class="{ on: tabs.activeKey === '/ops/extension-releases' }" @click="tabs.open('/ops/extension-releases')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3 3h10v10H3z"/><path d="M5 6h6M5 9h6"/></svg>
            <span>插件发布</span>
          </RouterLink>
          <RouterLink to="/ops/market-collector" :class="{ on: tabs.activeKey === '/ops/market-collector' }" @click="tabs.open('/ops/market-collector')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="8" cy="8" r="5.5" /><path d="M8 5v3l2 2" /></svg>
            <span>市场采集</span>
          </RouterLink>
          <RouterLink to="/ops/commission-rates" :class="{ on: tabs.activeKey === '/ops/commission-rates' }" @click="tabs.open('/ops/commission-rates')">
            <!-- 佣金费率📊：三档阶梯（类目×价格档三档费率，与卖家端定价图标区分） -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3 13V9M8 13V6M13 13V3" /><path d="M2.5 13.5h11" /></svg>
            <span>佣金费率</span>
          </RouterLink>
          <RouterLink to="/ops/provider-keys" :class="{ on: tabs.activeKey === '/ops/provider-keys' }" @click="tabs.open('/ops/provider-keys')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="5" cy="8" r="2.2" /><path d="M7.2 8h5.8" /><path d="M11 6.2v3.6" /><path d="M9 6.2v3.6" /></svg>
            <span>服务商密钥</span>
          </RouterLink>
          <RouterLink to="/ops/fee-profiles" :class="{ on: tabs.activeKey === '/ops/fee-profiles' }" @click="tabs.open('/ops/fee-profiles')">
            <!-- 费率配置💰：价签（与卖家端「费率」同型图标，但这里是写入口） -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><circle cx="8" cy="8" r="5.5" /><path d="M10 6.5c-.4-.7-1.2-1-2-1-1.2 0-2 .6-2 1.4 0 1.9 4 .9 4 2.7 0 .9-.9 1.5-2.1 1.5-.9 0-1.7-.4-2.1-1.1" /><path d="M8 4v1M8 11v1" /></svg>
            <span>费率配置</span>
          </RouterLink>
          <RouterLink to="/ops/shipping-rates" :class="{ on: tabs.activeKey === '/ops/shipping-rates' }" @click="tabs.open('/ops/shipping-rates')">
            <!-- 物流分段报价📦：包裹+阶梯段（P0-b 运费来源，保本价方程的运费一项） -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M2 5.5h9l3 2.5-3 2.5H2z" /><path d="M5 5.5v5" /><path d="M2 3.5h7v2" /></svg>
            <span>运费预估参数</span>
          </RouterLink>
          <RouterLink to="/ops/data-config" :class="{ on: tabs.activeKey === '/ops/data-config' }" @click="tabs.open('/ops/data-config')">
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M3 3h10v10H3z" /><path d="M5 6h6M5 9h6" /><circle cx="12" cy="4" r="1.2" fill="currentColor" stroke="none" /></svg>
            <span>数据与防护配置</span>
          </RouterLink>
        </nav>

        <!-- 🔴 货代端 <b>桌面后台</b>菜单（doc/59 §零：货代管理后台复用 seller 应用）。
             与 isOperator 同型：左菜单 + 多页签。货代老板 / 财务登录后看到的就是这两项。
             ⚠️ 这是「别走进一个必然报错的界面」，⛔ 不是权限 —— `/fwd/v1/**` 靠 FORWARDER 令牌守。
             PWA 作业入口 /tasks 也挂在「仓库」分组下 —— 仓库现场仍可从这里切去作业页。 -->
        <nav v-else-if="isForwarder" class="menu">
          <span class="sec">资金</span>
          <RouterLink to="/fwd/settlement" :class="{ on: tabs.activeKey === '/fwd/settlement' }" @click="tabs.open('/fwd/settlement')">
            <!-- 我的结算单💸：结算单（与运营端「货代结算」同型图标，但这是货代自己的应收账款） -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3h10v10H3z" /><path d="M3 7h10" /><path d="M6 10l-1.2 1.2" /><path d="M10 10l1.2 1.2" /><path d="M6 10h4" /></svg>
            <span>我的结算单</span>
          </RouterLink>
          <RouterLink to="/fwd/payee" :class="{ on: tabs.activeKey === '/fwd/payee' }" @click="tabs.open('/fwd/payee')">
            <!-- 收款信息🏦：银行卡 -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="4" width="12" height="8" rx="1.2" /><path d="M2 6.5h12" /><path d="M4 9.5h2.5" /></svg>
            <span>收款信息</span>
          </RouterLink>
          <span class="sec">仓库</span>
          <RouterLink to="/fwd/qr-login" :class="{ on: tabs.activeKey === '/fwd/qr-login' }" @click="tabs.open('/fwd/qr-login')">
            <!-- 首次设备授权：只确认仓库设备请求，不生成可直接兑换的短码。 -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><rect x="2.5" y="2.5" width="3.5" height="3.5" /><rect x="10" y="2.5" width="3.5" height="3.5" /><rect x="2.5" y="10" width="3.5" height="3.5" /><path d="M9 9h1.5v1.5" /><path d="M13 9v1.5" /><path d="M9 13h1.5" /><path d="M12.5 12.5h1.5" /></svg>
            <span>设备授权</span>
          </RouterLink>
          <RouterLink to="/fwd/sessions" :class="{ on: tabs.activeKey === '/fwd/sessions' }" @click="tabs.open('/fwd/sessions')">
            <!-- 设备管理🚪：退出/门（应急踢出仓库设备登录态，员工离职/手机丢了/换设备） -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 2.5h6v11H3z" /><path d="M9 8h4.5" /><path d="M11.5 5.5 14 8l-2.5 2.5" /></svg>
            <span>设备管理</span>
          </RouterLink>
          <RouterLink to="/tasks" :class="{ on: tabs.activeKey === '/tasks' }">
            <!-- 作业入口📦：仓库作业 PWA（切去 ForwarderFrame 全屏外壳） -->
            <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2l5.5 3v6L8 14 2.5 11V5z" /><path d="M2.7 5.2 8 8l5.3-2.8" /><path d="M8 8v6" /></svg>
            <span>作业入口</span>
          </RouterLink>
        </nav>

        <!--
          🔴🔴 两级菜单（竞品 f-050/f-088 对标，规格 §8）：
          一级=可折叠分组（按钮，aria-expanded），二级=页面项。
          进哪页展开哪组（watch route.path），展开态存 localStorage，用户手收的组不被路由翻回。
          「上架记录」指向 /listing/records（本批占位页，第三版换真页）——
          纪律不变：菜单项不灰、不「敬请期待」，页面自己解释现状。
        -->
        <nav v-else class="menu">
          <RouterLink to="/workbench" :class="{ on: tabs.activeKey === '/workbench' }" @click="tabs.open('/workbench')">
            <!-- 工作台📊：四方格仪表盘 -->
            <span class="ic ic-c5"><svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><rect x="2.5" y="2.5" width="4.5" height="4.5" rx="1" /><rect x="9" y="2.5" width="4.5" height="4.5" rx="1" /><rect x="2.5" y="9" width="4.5" height="4.5" rx="1" /><rect x="9" y="9" width="4.5" height="4.5" rx="1" /></svg></span>
            <span>概览</span>
          </RouterLink>
          <RouterLink to="/intelligence" :class="{ on: tabs.activeKey === '/intelligence' }" @click="tabs.open('/intelligence')">
            <!-- 经营情报：趋势线 + 风险点 -->
            <span class="ic ic-c7"><svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 12.8 6 9.2l2.3 1.9 5.2-6" /><circle cx="13.5" cy="5.1" r="1" /><path d="M2.5 3v10h11" /></svg></span>
            <span>分析</span>
          </RouterLink>

          <!-- ══ 商品 ▾ ══ -->
          <div class="grp" :class="{ open: menuOpen.products }">
            <button type="button" class="grp-head" :aria-expanded="menuOpen.products" @click="toggleMenuGroup('products')">
              <span class="ic ic-c1"><svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2l5.5 3v6L8 14 2.5 11V5z" /><path d="M2.7 5.2 8 8l5.3-2.8" /><path d="M8 8v6" /></svg></span>
              <span>商品</span>
              <svg class="chev" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6.2 4 3.6 4-3.6" /></svg>
            </button>
            <div v-show="menuOpen.products" class="grp-body">
              <RouterLink to="/products" :class="{ on: tabs.activeKey === '/products' && route.query.view !== 'delisted' && route.query.view !== 'favorite' }" @click="tabs.open('/products')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 2.5h6L13.5 7.5 8 13 2.5 7.5z" /><circle cx="5.5" cy="5.5" r="1" /></svg>
                <span>在线商品</span>
              </RouterLink>
              <RouterLink to="/collection" :class="{ on: tabs.activeKey === '/collection' }" @click="tabs.open('/collection')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3h11v10h-11z" /><path d="M2.5 8h3l1 1.5h3l1-1.5h3" /></svg>
                <span>采集箱</span>
              </RouterLink>
              <RouterLink to="/listing/records" :class="{ on: tabs.activeKey === '/listing/records' }" @click="tabs.open('/listing/records')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 2.5h10v11H3z" /><path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" /></svg>
                <span>上架记录</span>
              </RouterLink>
              <RouterLink :to="{ path: '/products', query: { view: 'delisted' } }" :class="{ on: route.path === '/products' && route.query.view === 'delisted' }" @click="tabs.open('/products')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2l5.5 3v6L8 14 2.5 11V5z" /><path d="M3.5 3.5l9 9" /></svg>
                <span>下架重上</span>
              </RouterLink>
              <RouterLink :to="{ path: '/products', query: { view: 'favorite' } }" :class="{ on: route.path === '/products' && route.query.view === 'favorite' }" @click="tabs.open('/products')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3l1.8 3.6 4 .6-2.9 2.8.7 4L8 12.1 4.4 14l.7-4L2.2 7.2l4-.6z" /></svg>
                <span>收藏夹</span>
              </RouterLink>
            </div>
          </div>

          <!-- ══ 选品 ▾ ══ -->
          <div class="grp" :class="{ open: menuOpen.selection }">
            <button type="button" class="grp-head" :aria-expanded="menuOpen.selection" @click="toggleMenuGroup('selection')">
              <span class="ic ic-c2"><svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3h11l-4.2 5v4.2L6.7 14V8z" /></svg></span>
              <span>选品</span>
              <svg class="chev" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6.2 4 3.6 4-3.6" /></svg>
            </button>
            <div v-show="menuOpen.selection" class="grp-body">
              <RouterLink to="/selection/tasks" :class="{ on: tabs.activeKey === '/selection' }" @click="tabs.open('/selection/tasks')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3h11l-4.2 5v4.2L6.7 14V8z" /></svg>
                <span>选品任务</span>
              </RouterLink>
              <RouterLink v-if="phase2.SELECTION_ANALYTICS" to="/selection/analytics" :class="{ on: tabs.activeKey === '/selection/analytics' }" @click="tabs.open('/selection/analytics')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 12.5h11M4 10V7M8 10V3M12 10V5" /></svg>
                <span>类目分析</span>
              </RouterLink>
            </div>
          </div>

          <!-- ══ 货源 ▾（B9：1688 诚实降级——两页都可见真实授权状态，pilot 关闭时整组不渲染）══ -->
          <div v-if="phase2.ALIBABA1688" class="grp" :class="{ open: menuOpen.sourcing }">
            <button type="button" class="grp-head" :aria-expanded="menuOpen.sourcing" @click="toggleMenuGroup('sourcing')">
              <span class="ic ic-c8"><svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 4h11v8h-11z" /><path d="M5 4V2.5h6V4M5 8h6" /></svg></span>
              <span>货源</span>
              <svg class="chev" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6.2 4 3.6 4-3.6" /></svg>
            </button>
            <div v-show="menuOpen.sourcing" class="grp-body">
              <RouterLink v-if="phase2.ALIBABA1688" to="/sourcing/1688/auth" :class="{ on: tabs.activeKey === '/sourcing/1688/auth' }" @click="tabs.open('/sourcing/1688/auth')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><circle cx="5" cy="8" r="2.2" /><path d="M7.2 8h5.8" /><path d="M11 6.2v3.6" /><path d="M9 6.2v3.6" /></svg>
                <span>1688账号授权</span>
              </RouterLink>
              <RouterLink v-if="phase2.ALIBABA1688" to="/sourcing/1688" :class="{ on: tabs.activeKey === '/sourcing/1688' }" @click="tabs.open('/sourcing/1688')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 4h11v8h-11z"/><path d="M5 4V2.5h6V4M5 8h6M8 6v4"/></svg>
                <span>1688选品铺货</span>
              </RouterLink>
            </div>
          </div>

          <!-- ══ 促销 ▾（B8 三页常驻 —— Pilot 只关写不关看；自动移出随 phase2 开关）══ -->
          <div class="grp" :class="{ open: menuOpen.promotion }">
            <button type="button" class="grp-head" :aria-expanded="menuOpen.promotion" @click="toggleMenuGroup('promotion')">
              <span class="ic ic-c3"><svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5v3a2 2 0 0 1-2 2H8L4.5 12V6.5L8 3h2.5a2 2 0 0 1 2 .5z" /><path d="M4.5 12 3 13.5" /></svg></span>
              <span>促销</span>
              <svg class="chev" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6.2 4 3.6 4-3.6" /></svg>
            </button>
            <div v-show="menuOpen.promotion" class="grp-body">
              <RouterLink to="/promotions" :class="{ on: tabs.activeKey === '/promotions' }" @click="tabs.open('/promotions')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5v3a2 2 0 0 1-2 2H8L4.5 12V6.5L8 3h2.5a2 2 0 0 1 2 .5z" /><path d="M4.5 12 3 13.5" /></svg>
                <span>促销活动</span>
              </RouterLink>
              <RouterLink v-if="phase2.AUTO_PROMOTION_REMOVAL" to="/phase2/auto-promotion-removal" :class="{ on: tabs.activeKey === '/phase2/auto-promotion-removal' }" @click="tabs.open('/phase2/auto-promotion-removal')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M12.5 3.5v3a2 2 0 0 1-2 2H8L4.5 12V6.5L8 3h2.5a2 2 0 0 1 2 .5z" /><path d="m9 6.5 2 2 3-3.5" /></svg>
                <span>自动移出</span>
              </RouterLink>
            </div>
          </div>

          <!-- ══ 订单 ▾ ══ -->
          <div class="grp" :class="{ open: menuOpen.orders }">
            <button type="button" class="grp-head" :aria-expanded="menuOpen.orders" @click="toggleMenuGroup('orders')">
              <span class="ic ic-c4"><svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3h10v10H3z" /><path d="M3 6.5h10M6 3v10" /></svg></span>
              <span>订单</span>
              <svg class="chev" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6.2 4 3.6 4-3.6" /></svg>
            </button>
            <div v-show="menuOpen.orders" class="grp-body">
              <RouterLink to="/orders" :class="{ on: tabs.activeKey === '/orders' }" @click="tabs.open('/orders')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2l5.5 3v6L8 14 2.5 11V5z" /><path d="M2.7 5.2 8 8l5.3-2.8" /><path d="M8 8v6" /></svg>
                <span>我的订单</span>
              </RouterLink>
              <RouterLink to="/returns" :class="{ on: tabs.activeKey === '/returns' }" @click="tabs.open('/returns')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3h10v10H3z" /><path d="m5 8 2 2 4-4" /></svg>
                <span>退货案件</span>
              </RouterLink>
              <!-- 订单消息（B7 竞品「消息模板」同位）：模板五列 + 发送记录独立页。门槛说明在页内如实展示 -->
              <RouterLink to="/messages" :class="{ on: tabs.activeKey === '/messages' }" @click="tabs.open('/messages')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3.5h11v7h-6L4 13.5V10.5H2.5z" /><path d="M5 6.5h6M5 8.5h4" /></svg>
                <span>消息</span>
              </RouterLink>
              <RouterLink to="/messages/records" :class="{ on: tabs.activeKey === '/messages/records' }" @click="tabs.open('/messages/records')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 2.5h10v11H3z" /><path d="M5.5 5.5h5M5.5 8h5M5.5 10.5h3" /></svg>
                <span>发送记录</span>
              </RouterLink>
              <RouterLink v-if="phase2.ADVANCED_MESSAGING" to="/phase2/advanced-messaging" :class="{ on: tabs.activeKey === '/phase2/advanced-messaging' }" @click="tabs.open('/phase2/advanced-messaging')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3.5h11v7h-6L4 13.5V10.5H2.5z" /><path d="M5 6.5h6M5 8.5h4" /></svg>
                <span>消息策略</span>
              </RouterLink>
              <!-- B10：订单数据大屏（订单组）—— 订单域统计契约页；与下方 SALES_SCREEN 销售大屏分开 -->
              <RouterLink to="/datascreen" :class="{ on: tabs.activeKey === '/datascreen' }" @click="tabs.open('/datascreen')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 13V3M2.5 13h11M5 10.5V7M8.5 10.5V4M12 10.5V6" /></svg>
                <span>数据大屏</span>
              </RouterLink>
              <RouterLink v-if="phase2.SALES_SCREEN" to="/reports/sales" :class="{ on: tabs.activeKey === '/reports/sales' }" @click="tabs.open('/reports/sales')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 12.8 6 9.2l2.3 1.9 5.2-6" /><circle cx="13.5" cy="5.1" r="1" /><path d="M2.5 3v10h11" /></svg>
                <span>销售大屏</span>
              </RouterLink>
            </div>
          </div>

          <!-- ══ 店铺 ▾（2026-09-09 用户指令：水印跟随店铺编辑，独立菜单项已撤；
               模板管理 /watermarks 保留路由，从店铺详情「管理模板」进）══ -->
          <div class="grp" :class="{ open: menuOpen.shops }">
            <button type="button" class="grp-head" :aria-expanded="menuOpen.shops" @click="toggleMenuGroup('shops')">
              <span class="ic ic-c6"><svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3h11l-.8 3H3.3z" /><path d="M3.5 6v7.5h9V6" /><path d="M6.5 13.5v-3h3v3" /></svg></span>
              <span>店铺</span>
              <svg class="chev" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6.2 4 3.6 4-3.6" /></svg>
            </button>
            <div v-show="menuOpen.shops" class="grp-body">
              <RouterLink to="/shops" :class="{ on: tabs.activeKey === '/shops' }" @click="tabs.open('/shops')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M2.5 3h11l-.8 3H3.3z" /><path d="M3.5 6v7.5h9V6" /><path d="M6.5 13.5v-3h3v3" /></svg>
                <span>店铺列表</span>
              </RouterLink>
              <!-- 店铺分组（竞品 batch-02 对标）：多对多分组 + 默认库存/仓库 -->
              <RouterLink to="/shop-groups" :class="{ on: tabs.activeKey === '/shop-groups' }" @click="tabs.open('/shop-groups')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><rect x="2.5" y="2.5" width="4.5" height="4.5" rx="1" /><rect x="9" y="2.5" width="4.5" height="4.5" rx="1" /><rect x="2.5" y="9" width="4.5" height="4.5" rx="1" /><rect x="9" y="9" width="4.5" height="4.5" rx="1" /></svg>
                <span>分组管理</span>
              </RouterLink>
            </div>
          </div>

          <!-- ══ 工具 ▾（竞品「工具」组同位：定价/钱包类）══ -->
          <div class="grp" :class="{ open: menuOpen.tools }">
            <button type="button" class="grp-head" :aria-expanded="menuOpen.tools" @click="toggleMenuGroup('tools')">
              <span class="ic ic-c9"><svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M9 2.5 13.5 7 7.5 13 3 8.5z" /><path d="M6 5.5 10.5 10" /></svg></span>
              <span>工具</span>
              <svg class="chev" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 6.2 4 3.6 4-3.6" /></svg>
            </button>
            <div v-show="menuOpen.tools" class="grp-body">
              <RouterLink to="/pricing" :class="{ on: tabs.activeKey === '/pricing' }" @click="tabs.open('/pricing')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.5" /><path d="M10 6.5c-.4-.7-1.2-1-2-1-1.2 0-2 .6-2 1.4 0 1.9 4 .9 4 2.7 0 .9-.9 1.5-2.1 1.5-.9 0-1.7-.4-2.1-1.1" /><path d="M8 4v1M8 11v1" /></svg>
                <span>店铺费率</span>
              </RouterLink>
              <RouterLink to="/tools/pricing" :class="{ on: tabs.activeKey === '/tools/pricing' }" @click="tabs.open('/tools/pricing')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><rect x="3" y="2" width="10" height="12" rx="1.5" /><path d="M5 5h6M5 8h2.5M5 11h2.5M10 8.5v3" /></svg>
                <span>定价与利润</span>
              </RouterLink>
              <RouterLink to="/wallet" :class="{ on: tabs.activeKey === '/wallet' }" @click="tabs.open('/wallet')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="4" width="12" height="8" rx="1.2" /><path d="M2 6.5h12" /><path d="M4 9.5h2.5" /></svg>
                <span>钱包</span>
              </RouterLink>
              <RouterLink to="/billing" :class="{ on: tabs.activeKey === '/billing' }" @click="tabs.open('/billing')">
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3" width="12" height="10" rx="1.2" /><path d="M2 6.5h12M5 9.5h3" /></svg>
                <span>套餐与用量</span>
              </RouterLink>
              <!-- B10 外链：外部服务，新窗口打开；🔴 rel=noopener（+noreferrer）是红线。
                   不是路由页 —— 不进 Tab 系统，title 标明「外部服务」。 -->
              <a
                href="https://chat.deepseek.com"
                target="_blank"
                rel="noopener noreferrer"
                title="DeepSeek（外部服务，新窗口打开）"
              >
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3h10v10H3z" /><path d="M5.5 6.5h5M5.5 9.5h3" /></svg>
                <span>DeepSeek<svg class="ext-icon" viewBox="0 0 16 16" aria-hidden="true"><path d="M6.5 3H13v6.5" /><path d="M13 3 7 9" /><path d="M12 9.5V13H3V4h3.5" /></svg></span>
              </a>
              <a
                href="https://www.17track.net/zh-cn"
                target="_blank"
                rel="noopener noreferrer"
                title="全球物流查询（外部服务，新窗口打开）"
              >
                <svg class="micon" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="5.5" /><path d="M2.5 8h11" /><path d="M8 2.5c1.7 1.6 2.6 3.4 2.6 5.5S9.7 12.4 8 13.5c-1.7-1.1-2.6-3.4-2.6-5.5S6.3 4.1 8 2.5z" /></svg>
                <span>全球物流查询<svg class="ext-icon" viewBox="0 0 16 16" aria-hidden="true"><path d="M6.5 3H13v6.5" /><path d="M13 3 7 9" /><path d="M12 9.5V13H3V4h3.5" /></svg></span>
              </a>
            </div>
          </div>

        </nav>

        <span class="nav-overflow-cue" aria-hidden="true">›</span>

        <div class="foot">
          <template v-if="who">
            <!-- 通知铃铛：未读数角标 + 跳消息中心 -->
            <RouterLink v-if="hasTenantNotifications" to="/notifications" class="bell" :title="unreadCount > 0 ? `${unreadCount} 条未读` : '消息中心'">
              <svg viewBox="0 0 16 16" class="bell-icon" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.4">
                <path d="M8 2a3.5 3.5 0 0 0-3.5 3.5V9L3 11h10l-1.5-2V5.5A3.5 3.5 0 0 0 8 2z" />
                <path d="M6.5 12.5a1.5 1.5 0 0 0 3 0" />
              </svg>
              <span v-if="unreadCount > 0" class="badge">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
            </RouterLink>
            <div class="who">{{ who }}</div>
            <button class="out" @click="doLogout">退出</button>
          </template>
          <RouterLink v-else class="out" to="/login">登录</RouterLink>
        </div>
      </aside>

      <main class="main">
        <div v-if="phase2Unavailable || typeof route.query.featureRetry === 'string'"
          class="route-notice" role="alert" data-testid="feature-load-error">
          部分功能配置暂时无法读取，对应入口暂不可用，其他功能可继续使用。
          <button type="button" class="btn ghost" :disabled="phase2Loading" @click="retryPhase2">
            {{ phase2Loading ? '正在重试…' : '重试加载' }}
          </button>
        </div>
        <div
          v-if="route.query.notice === 'ai-config-parked'"
          class="route-notice"
          role="status"
          aria-label="AI 配置未开放"
        >
          AI 配置尚未开放，已返回数据与防护配置。当前版本不会加载或修改 AI 模型与密钥。
        </div>
        <!-- 🔴 多 Tab：tab 栏贴顶，内容区在下。tab 切换靠上方的 watch($route) + TabBar 内的 router.push。
             右侧常驻 chips（竞品 f-088 顶栏同位，2026-09-09 用户定稿；§12 补齐为三枚：
             下载插件 = 动作，套餐与用量 / 余额 = 高频入口）。
             🔴 这三枚是**常驻快捷入口**，左栏「工具」组里的同名菜单项**照旧保留**（⛔ 不删）。
             ⚠️ 余额这一枚只做**入口搬移**：⛔ 顶栏不显示金额 —— 后端 `GET /api/v1/account/balance`
                确实存在（钱包页在用），但在外壳里再拉一次会得到一个**只在刷新时才更新**的数字，
                而充值/退款都会立刻改变它 ⇒ 常驻位上的过期金额比没有金额更糟。
                所以如实标注指向「我的钱包」页，⛔ 不编数字、也不拿 0 充数。 -->
        <div class="tabstrip-row">
          <TabBar class="tabstrip grow" />
          <div class="top-chips" aria-label="常驻快捷入口">
            <button
              type="button"
              class="plugin-dl-top"
              :disabled="extensionDownloading"
              :title="extensionDownloading ? '正在核验…' : '下载浏览器插件（SHA-256 核验）'"
              @click="downloadExtension"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M8 2v7M5 6.2 8 9.2l3-3M3 13h10" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              {{ extensionDownloading ? '核验中…' : '下载插件' }}
            </button>
            <RouterLink
              v-if="isSellerView"
              to="/billing"
              class="top-chip"
              title="套餐与用量"
              @click="tabs.open('/billing')"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="3" width="12" height="10" rx="1.2" /><path d="M2 6.5h12M5 9.5h3" /></svg>
              <span>套餐与用量</span>
            </RouterLink>
            <RouterLink
              v-if="isSellerView"
              to="/wallet"
              class="top-chip"
              title="余额与流水在「我的钱包」页（顶栏不显示金额，避免过期数字）"
              @click="tabs.open('/wallet')"
            >
              <svg viewBox="0 0 16 16" aria-hidden="true"><rect x="2" y="4" width="12" height="8" rx="1.2" /><path d="M2 6.5h12" /><path d="M4 9.5h2.5" /></svg>
              <span>余额</span>
            </RouterLink>
          </div>
        </div>
        <div class="content">
          <!-- 路由切换渐入渐出：mode=out-in 让旧页先淡出、新页再淡入，避免两层重叠错位。
               🔴 key 用 route.path（2026-09-09 用户反馈修订）：换页才换组件实例；
               同页 query 变化（筛选/翻页）由各页自己的 watch 响应，⛔ 不整页重挂 ——
               用 fullPath 做 key 时每次下拉筛选都会整页淡出重挂+骨架屏 = 「整页闪」。

               🔴🔴 keep-alive（DESIGN §12 外壳 A 案，2026-09-11）：把「受页签管理的路由视图」
               包进 KeepAlive ⇒ **切 tab 回来时筛选条件、滚动位置、已加载数据都还在**
               （§11「筛选绝不整页重挂」由它根治，而不再只靠 key 用 path 这一层规避）。

               ① **名单来自 store，不在这里另写一份**：`:include` = `tabs.cacheableNames()`
                  =「打开中的页签 × stores/tabs.ts 白名单内的路由」的组件名。
                  ⛔ 非页签路径（blank 布局的登录/注册、货代 PWA `/tasks`、未登记的
                  `/fwd/sessions` `/ops/shipping-rates` 等）名字不在名单里 ⇒ KeepAlive
                  不缓存它们（`include` 不匹配就原样返回 vnode），行为与加此前**完全一致**。
               ② 为什么用 `include`（组件名）而不是路由 `meta.keepAlive`：
                  KeepAlive 的 `include` 只认组件名（Vue 的既有约定），而名字在**编译期**
                  就由文件名注入（`__name`），从 `router.getRoutes()` 的组件对象上直接读得到
                  —— 零维护且和路由表不漂移；写 `meta.keepAlive` 则要在 70 条路由上手写
                  第二份「该不该缓存」的名单，还会和 store 的白名单两处打架。
                  ⚠️ 懒加载组件是**导航完成时**才被写回路由记录的 ⇒ 下面这个 computed
                  必须把 `route.fullPath` 也读进依赖（见 cachedViewNames 注释）。
               ③ **关闭页签即驱逐缓存**：`:include` 由 store 的页签数组推导，关掉一条
                  页签 ⇒ 名单收缩 ⇒ KeepAlive 立即 pruneCache 掉该页缓存（重开是干净态，
                  不会看到旧筛选）。
               ④ `:max="MAX_CACHED_VIEWS"` 兜底，防缓存无上限增长。
               ⑤ 过渡照旧：`<transition>` 是 KeepAlive 的外层，Vue 的 BaseTransition 会
                  解包 KeepAlive 取到**内层页面 vnode** 去比 key/type ⇒ 渐入渐出、
                  `mode="out-in"`、`.page-*` 类名全部不变（拆包逻辑在
                  runtime-core 的 `getInnerChild`）。 -->
          <router-view v-slot="{ Component: view, route: r }">
            <transition name="page" mode="out-in">
              <keep-alive :key="viewOwner" :include="cachedViewNames" :max="MAX_CACHED_VIEWS">
                <component :is="view" :key="r.path" />
              </keep-alive>
            </transition>
          </router-view>
        </div>
      </main>
    </div>

    <!-- 插件安装/更新指引：Chrome 不会自动替换同 ID 扩展，必须先移除旧版再加载新版文件夹 -->
    <div v-if="extensionGuideVisible" class="ext-guide-mask" @click.self="extensionGuideVisible = false">
      <div class="ext-guide" role="dialog" aria-label="插件安装指引">
        <header>
          <strong>插件 {{ extensionGuideVersion }} 安装 / 更新步骤</strong>
          <button type="button" @click="extensionGuideVisible = false">关闭</button>
        </header>
        <ol>
          <li>把刚下载的 <code>crossborder-extension-{{ extensionGuideVersion }}.zip</code> <b>解压</b>到一个长期保留的文件夹（不要放在「下载」里随手清理的地方）。</li>
          <li>打开 <code>chrome://extensions</code>，开启右上角「开发者模式」。</li>
          <li>已有旧版时，优先替换原解压目录的文件并点「重新加载」；需要移除旧安装前，先保存本机未同步商品，避免丢失本机数据。</li>
          <li>点「加载已解压的扩展程序」，选择<b>解压出来的文件夹</b>（不是 zip 文件本身）。</li>
          <li>在同一浏览器的管理后台登录，插件会自动同步当前账号，无需手动连接或配对码。</li>
          <li>管理后台退出或切换账号后，插件跟随清除或更新登录态。后台会话仍有效时，商品页会自动恢复插件登录；后台会话失效时再正常登录。</li>
        </ol>
        <footer>安装并登录后台后，到 Ozon 商品页查看数据；同步失败会显示原因，不需要输入配对码，也不要填写账号密码给插件。</footer>
      </div>
    </div>
  </div>
</template>

<style scoped>
.root {
  min-height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--canvas);
}

.route-notice {
  margin: var(--shell-gap) var(--shell-gap) 0;
  padding: 10px 14px;
  border: 1px solid var(--warn);
  border-radius: var(--r-sm);
  background: var(--warn-bg);
  color: var(--text);
  font-size: 13px;
}

/* 🔴 地面露一圈（--shell-gap），左菜单与内容区之间也是同一个间距 ——
   卡与卡之间的距离和卡与边缘的距离相等，整屏才像"摆上去的"而不是"塞进去的"。 */
.shell {
  flex: 1 1 auto;
  display: grid;
  grid-template-columns: var(--nav-width) minmax(0, 1fr);
  gap: var(--shell-gap);
  padding: var(--shell-gap);
  align-items: start;
}

/* ══ 左菜单 ═══════════════════════════════════════════════════════════ */
.nav {
  position: sticky;
  top: var(--shell-gap);
  /* 内容再长，菜单自己不跟着滚 —— 它是导航，不是内容 */
  height: calc(100vh - var(--shell-gap) * 2);
  display: flex;
  flex-direction: column;
  padding: 14px 0 12px;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r-lg);
}

.brand {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 0 18px 14px;
  text-decoration: none;
}

.mark {
  width: 20px;
  flex: 0 0 auto;
}

.mark line {
  fill: none;
  stroke: url(#jh-brand-grad);   /* 2026-09-18 V3：品牌渐变（defs 在 .mark svg 内） */
  stroke-width: 3;
  stroke-linecap: square;
}

.brand b {
  font-size: 14px;
  font-weight: 600;
  color: var(--text);
  letter-spacing: -0.005em;
}

.menu {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
}

.sec {
  font-size: 11px;
  letter-spacing: 0.14em;
  color: var(--text-3);
  padding: 12px 18px 5px;
}

/* 🔴 菜单项是**圆角胶囊**，不是满宽色条：左右各留 8px，
   选中那块才像"被拿起来的一项"，⛔ 而不是"背景变了一条" */
.menu a {
  display: flex;
  align-items: center;
  gap: 9px;
  height: 34px;
  margin: 0 8px;
  padding: 0 10px;
  color: var(--text-2);
  text-decoration: none;
  font-size: 15px;
  border-radius: var(--r-sm);
  transition: color 0.14s, background 0.14s;
}

/* 内联 SVG 图标：16px 线条风格，跟文字同色（currentColor）。
   ⛔ 不引图标库 —— 用内联 SVG 是为了零新依赖 + 选中态自动跟着 a 的 color 走。 */
.micon {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  /* 描边跟随文字色：未选中是 --text-2，选中是墨色（--text），
     hover 是 --text —— 全靠 currentColor，无需为每种态各写一遍。 */
  fill: none;
  stroke: currentColor;
  stroke-width: 1.3;
  stroke-linejoin: round;
  stroke-linecap: round;
}

/* 价签那一颗的「¥」实心一点，需要单独描细一点 */
.micon + span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* B10 外链角标：小号「↗」，与文字同色（currentColor），标注这是跳出系统的外部服务 */
.ext-icon {
  width: 10px;
  height: 10px;
  margin-left: 4px;
  vertical-align: 0;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linejoin: round;
  stroke-linecap: round;
}

.menu a:hover {
  color: var(--text);
  background: var(--brand-wash);
}

/* 🔴 选中 = 底变蓝、**字保持墨色**，⛔ 不是蓝底蓝字（见 global.css 那条硬规矩）。
   用 `.on`（= 当前激活 tab 的 key）判，⛔ 不再用 router-link-active ——
   子路由（如 `/orders/123`）不会让 router-link-active 生效，而 tab key 会。
   ⚠️ 唯一的例外是**菜单图标**：选中态下它翻成强调蓝（--brand-500）。
   蓝底 + 墨字 + 一个蓝色图标不会糊成「一片蓝」，因为图标只是 16px 的小色块，
   而不是文字那一片 —— 它起到的是「定位选中项」的作用。 */
.menu a.on {
  color: var(--text);
  background: var(--brand-50);
  box-shadow: inset 0 0 0 1px var(--brand-100);
  font-weight: 600;
}

.menu a.on .micon {
  stroke: var(--brand-500);
}

/* ══ tabstrip 行 + 顶栏常驻 chips（竞品 f-088 顶栏同位；§12：下载插件/套餐/余额）══
   行的职责没变：tab 栏占满，右侧一串常驻 chip。
   ⚠️ `flex-wrap: wrap`：chip 有宽度（窄屏/多页签时溢出），宁可掉到第二行，
      ⛔ 不把 tab 挤到看不见 —— 与全文件「窄屏只保证别烂掉」同一条口径。 */
.tabstrip-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.tabstrip-row .grow { flex: 1 1 auto; min-width: 0; }

.top-chips {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 4px;
}

/*
  chip 的底 / 描边 / 字色**全部走令牌**（§10 暖调 + §12「令牌单一来源」）：
   底 = 卡面、描边 = --line、字 = --text-2；hover 一律**变亮**（§11：hover 变亮不变深）：
   底转 --brand-wash（极淡品牌洗色）、描边转 --brand-100、字转墨色。
  色相纪律（§10「蓝=可交互」）：三枚 chip 同属蓝族，**靠图标形状区分，不靠色相**。
  ⛔ 这里不出现任何私设色值。
*/
.top-chip,
.plugin-dl-top {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 9px;
  background: var(--surface);
  color: var(--text-2);
  font-size: 12.5px;
  font-weight: 600;
  font-family: inherit;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.14s ease, border-color 0.14s ease, color 0.14s ease, transform 0.14s ease;
}
.top-chip svg,
.plugin-dl-top svg { width: 13px; height: 13px; flex: 0 0 auto; }
/* hover：底**变亮**（§11 铁律，⛔ 不变深）+ 描边加深 + 1px 轻抬升（§11「胶囊用轻抬升」） */
.top-chip:hover { background: var(--brand-wash); border-color: var(--brand-100); color: var(--text); transform: translateY(-1px); }
.top-chip:focus-visible { outline: 2px solid var(--brand-100); outline-offset: 1px; }

/* 「下载插件」是这一行里唯一的**动作**（另两枚是跳转入口）：底给一档品牌洗色以示区别。
   ⚠️ 它此前 hover 写的是 #d8ebff —— 一个私设色，且比底更深，与 §11「hover 一律变亮」相反；
      现在换成 --brand-wash（比 --brand-50 更亮）并把颜色全部收敛到令牌。
   🔴 class 名保持 `plugin-dl-top`（既有布局契约测试断言的就是它，样式与 .top-chip 同一套）。 */
.plugin-dl-top {
  background: var(--brand-50);
  border-color: var(--brand-100);
  color: var(--brand-600);
}
.plugin-dl-top:hover:not(:disabled) { background: var(--brand-wash); border-color: var(--brand-100); transform: translateY(-1px); }
.plugin-dl-top:focus-visible { outline: 2px solid var(--brand-100); outline-offset: 1px; }
.plugin-dl-top:disabled { opacity: 0.6; cursor: wait; }

/* ══ 菜单图标色片（2026-09-09 用户反馈：图标来点颜色；2026-09-18 V3 扩九色）══
   §10 chip 体系：24px 圆角小片 + 九区各一色（商品蓝/选品绿/促销琥珀/订单珊瑚/
   概览靛/店铺紫/分析青/货源橙/工具粉，映射见 global.css --chip-1..9）+ 配置区中性；
   文字永远墨系，选中态仍走 brand-50 胶囊 —— 色在图标上，不在文字上 */
.ic {
  flex: 0 0 auto;
  width: 24px;
  height: 24px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ic .micon { width: 14px; height: 14px; }
.ic-c1 { background: var(--chip-1-bg); color: var(--chip-1-fg); }
.ic-c2 { background: var(--chip-2-bg); color: var(--chip-2-fg); }
.ic-c3 { background: var(--chip-3-bg); color: var(--chip-3-fg); }
.ic-c4 { background: var(--chip-4-bg); color: var(--chip-4-fg); }
.ic-c5 { background: var(--chip-5-bg); color: var(--chip-5-fg); }
.ic-c6 { background: var(--chip-6-bg); color: var(--chip-6-fg); }
.ic-c7 { background: var(--chip-7-bg); color: var(--chip-7-fg); }
.ic-c8 { background: var(--chip-8-bg); color: var(--chip-8-fg); }
.ic-c9 { background: var(--chip-9-bg); color: var(--chip-9-fg); }
.ic-cn { background: var(--surface-2); color: var(--text-2); }

/* ══ 两级菜单：分组头（按钮）+ 子项缩进 —— 竞品 f-050/f-088 对标 ══ */
.grp {
  margin: 0;
}

/* 分组头与普通菜单项同型（圆角胶囊），只是右侧多一枚箭头；
   🔴 用 button 不用 a：分组头没有目标页，点它只折叠/展开 */
.grp-head {
  display: flex;
  align-items: center;
  gap: 9px;
  width: calc(100% - 16px);
  height: 34px;
  margin: 0 8px;
  padding: 0 10px;
  border: 0;
  background: transparent;
  color: var(--text-2);
  font: inherit;
  font-size: 15px;
  text-align: left;
  border-radius: var(--r-sm);
  cursor: pointer;
  transition: color 0.14s, background 0.14s;
}

.grp-head:hover {
  color: var(--text);
  background: var(--brand-wash);
}

/* 展开箭头：折叠朝下、展开朝上，140ms 旋转 —— 不做弹跳 */
.grp-head .chev {
  margin-left: auto;
  width: 14px;
  height: 14px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.3;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: transform 0.14s ease;
  transform: rotate(-90deg);
}

.grp.open .grp-head .chev {
  transform: rotate(0deg);
}

/* 子项：与父级同样式，仅缩进 + 字号小一档（层级靠缩进不靠颜色） */
.grp-body {
  display: flex;
  flex-direction: column;
  padding: 1px 0 3px;
}

.grp-body a {
  font-size: 14px;
  padding-left: 33px;
}

/* 分组间距略大于普通项距，让「组」在纵向上成块 */
.grp + .grp,
.grp + a,
a + .grp,
.grp + .sec {
  margin-top: 2px;
}

.nav-overflow-cue {
  display: none;
}

.foot {
  margin: auto 8px 0;
  padding: 12px 10px 0;
  border-top: 1px solid var(--line);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.who {
  font-size: 11px;
  color: var(--text-3);
  flex: 1;
  min-width: 0;
}

/* 通知铃铛 */
.bell {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: var(--r-sm);
  color: var(--text-2);
  text-decoration: none;
  transition: background 0.14s, color 0.14s;
}
.bell:hover {
  background: var(--surface);
  color: var(--brand-600);
}
.bell-icon {
  width: 17px;
  height: 17px;
}
.badge {
  position: absolute;
  top: 1px;
  right: 1px;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  border-radius: 8px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 15px;
  text-align: center;
}

.out {
  display: inline-block;
  margin-top: 6px;
  background: var(--surface);
  border: 1px solid var(--line-strong);
  border-radius: var(--r-sm);
  color: var(--text-2);
  font-size: 11.5px;
  font-family: inherit;
  padding: 3px 11px;
  cursor: pointer;
  text-decoration: none;
  transition: border-color 0.14s, color 0.14s;
}

.out:hover {
  border-color: var(--brand-100);
  color: var(--brand-600);
}

.main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* 🔴 tab 栏是内容区的「顶」，贴在所有页签内容上方 */
.tabstrip {
  flex: 0 0 auto;
}

/* 内容区自己滚，tab 栏固定在顶（长页面里 tab 栏不被推走） */
.content {
  min-width: 0;
}

/* 🔴 路由切换过渡：渐入渐出，150ms。⚠️ 用 opacity 不用 transform ——
   transform 会让滚动锚点跳到顶（绝对定位 + 滚动恢复会打架），纯 opacity 最稳。
   ⛔ 不引动画库 —— transition + 几行 CSS 就够。 */
.page-enter-active,
.page-leave-active {
  transition: opacity 0.15s ease;
}

.page-enter-from,
.page-leave-to {
  opacity: 0;
}

/* 尊重「减少动效」：这一类用户面前，过渡退化成立即切换，仍然成立。 */
@media (prefers-reduced-motion: reduce) {
  .page-enter-active,
  .page-leave-active {
    transition: none;
  }
}

/*
  ⚠️ 卖家端是**桌面为主**的管理后台（global.css 开头就写明了不做移动断点）。
  这里唯一的窄屏处理是**别烂掉**：菜单横过来变成一条可横滚的胶囊行。
  ⛔ 它不是"移动端适配"，触摸尺寸的控件仍然属于货代端那个工程。
*/
@media (max-width: 900px) {
  .shell {
    grid-template-columns: 1fr;
  }

  .nav {
    position: static;
    height: auto;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    padding: 7px 8px;
    overflow-x: auto;
    border-radius: var(--r);
  }

  .brand {
    padding: 0 8px 0 6px;
  }

  .brand b {
    display: none;
  }

  .menu {
    flex-direction: row;
    flex: 0 0 auto;
    align-items: center;
    gap: 2px;
    overflow-y: visible;
  }

  .menu > a,
  .menu > .grp,
  .brand { flex: 0 0 auto; }

  .menu > .grp,
  .grp-body { display: flex; align-items: center; }

  .grp-head { width: auto; margin: 0; white-space: nowrap; flex: 0 0 auto; }
  .grp-body { flex-direction: row; margin: 0; padding: 0; gap: 2px; border-left: 1px solid var(--line); }
  .grp-body a { flex: 0 0 auto; padding: 0 12px; }

  .sec,
  .foot {
    display: none;
  }

  .menu a {
    margin: 0;
    white-space: nowrap;
  }

  .menu a span {
    overflow: visible;
    text-overflow: clip;
  }

  .nav-overflow-cue {
    position: sticky;
    right: -8px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    align-self: stretch;
    min-width: 44px;
    color: var(--brand-600);
    background: var(--surface);
    box-shadow: -8px 0 10px var(--surface);
    font-size: 22px;
    font-weight: 600;
  }
}
</style>

<style scoped>
.ext-guide-mask {
  position: fixed; inset: 0; z-index: 1000;
  background: rgba(53, 49, 43, 0.45);
  display: flex; align-items: center; justify-content: center;
}
.ext-guide {
  width: min(560px, calc(100vw - 48px));
  background: #fff; border-radius: 14px; padding: 20px 24px;
  box-shadow: 0 18px 50px rgba(87, 74, 56, 0.28);
  max-height: 82vh; overflow: auto;
}
.ext-guide header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
.ext-guide header button { border: none; background: none; cursor: pointer; color: #6b6459; font-size: 13px; }
.ext-guide ol { padding-left: 20px; display: grid; gap: 8px; color: #35312b; font-size: 14px; line-height: 1.65; }
.ext-guide code { background: #f4f1ec; border-radius: 4px; padding: 1px 6px; font-size: 12.5px; }
.ext-guide footer { margin-top: 12px; font-size: 12.5px; color: #857c6f; border-top: 1px solid #eee9e1; padding-top: 10px; }
</style>
